import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { downloadFile, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { confirmIfFarCompetence } from "@/lib/dateGuard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Download, Wallet, Check, Clock, AlertCircle, Wrench } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";

function brl(n) { return (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }); }

function fmtDate(iso) {
  if (!iso) return "—";
  try { const [y, m, d] = iso.substring(0, 10).split("-"); return `${d}/${m}/${y}`; } catch { return iso; }
}

function monthLabel(comp) {
  if (!comp) return "—";
  const [y, m] = comp.split("-");
  const names = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  return `${names[parseInt(m, 10) - 1] || m}/${y}`;
}

const STATUS_LABELS = {
  faturado: "Faturado",
  pronto_faturamento: "Pronto p/ faturar",
  documentos_gerados: "Docs gerados",
  aguardando_assinados: "Aguardando assinados",
  assinados_enviados: "Assinados enviados",
  divergencia_detectada: "Com divergência",
};

export default function FaturamentoPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "admin_master";
  const [summary, setSummary] = useState(null);
  const [cases, setCases] = useState([]);
  const [competence, setCompetence] = useState("");
  const [loading, setLoading] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const [hospitalId, setHospitalId] = useState("");

  useEffect(() => {
    api.get("/hospitals").then((r) => setHospitals(r.data || [])).catch(() => {});
  }, []);

  async function loadSummary() {
    try {
      const params = hospitalId ? { hospital_id: hospitalId } : {};
      const r = await api.get("/reports/billing/summary", { params });
      setSummary(r.data);
      if (!competence && r.data.by_competence?.length) {
        setCompetence(r.data.by_competence[0].competence);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function loadCases() {
    if (!competence) return;
    setLoading(true);
    try {
      const params = { competence };
      if (hospitalId) params.hospital_id = hospitalId;
      const r = await api.get("/reports/billing/cases", { params });
      setCases(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }

  function _isOddCompetence(c) {
    // Detects "Abr/0006", "Jan/1799", future > 2099 etc.
    if (!c) return false;
    const y = parseInt((c.split("-")[0] || ""), 10);
    return !y || y < 2000 || y > 2099;
  }

  async function fixCompetence() {
    if (!isAdmin || !competence) return;
    if (!_isOddCompetence(competence) && !(await confirm({ description: `A competência ${monthLabel(competence)} parece estar OK. ` +
      `Confirma que quer reatribuir mesmo assim?` }))) return;
    const total = (summary?.by_competence || []).find((c) => c.competence === competence)?.total || 0;
    const target = window.prompt(
      `Reatribuir ${total} casos de ${monthLabel(competence)} para qual competência?\n\n` +
      `Formato: AAAA-MM (ex: 2026-04)`,
      `2026-${(competence.split("-")[1] || "04")}`
    );
    if (!target) return;
    if (!/^\d{4}-\d{2}$/.test(target)) {
      toast.error("Formato inválido. Use AAAA-MM (ex: 2026-04).");
      return;
    }
    if (target === competence) {
      toast.info("Mesma competência, nada a fazer.");
      return;
    }
    if (!(await confirmIfFarCompetence(target, "competência destino"))) return;
    try {
      const r = await api.post("/admin/data/remap-competence", {
        from_competence: competence,
        to_competence: target,
      });
      const d = r.data || {};
      toast.success(
        `${d.affected_cases} caso(s) movido(s) de ${monthLabel(competence)} para ${monthLabel(target)}.`
      );
      // Reload everything to reflect the change
      setCompetence(target);
      await loadSummary();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao remapear");
    }
  }

  useEffect(() => { loadSummary(); /* eslint-disable-next-line */ }, [hospitalId]);
  useEffect(() => { loadCases(); /* eslint-disable-next-line */ }, [competence, hospitalId]);

  async function onExportBilling() {
    try {
      const params = {};
      if (competence) params.competence = competence;
      if (hospitalId) params.hospital_id = hospitalId;
      await downloadFile("/reports/billing.xlsx", `faturamento_${competence || "todas"}.xlsx`, params);
      toast.success("Relatório de faturamento gerado.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  const selected = useMemo(() => {
    if (!summary) return null;
    return summary.by_competence.find((c) => c.competence === competence) || null;
  }, [summary, competence]);

  const totals = useMemo(() => {
    let est = 0, ap = 0, fat = 0, rec = 0, adj = 0;
    for (const c of cases) { est += c.estimated; ap += c.approved; fat += c.billed; rec += c.received; adj += c.adjustments; }
    return { est, ap, fat, rec, adj };
  }, [cases]);

  return (
    <div className="space-y-5" data-testid="faturamento-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Faturamento</h1>
          <p className="text-sm text-slate-500 mt-1">Competências, glosas e repasse financeiro.</p>
        </div>
        <Button onClick={onExportBilling} className="bg-blue-700 hover:bg-blue-800 h-11 md:h-9 w-full sm:w-auto" data-testid="btn-export-billing">
          <Download className="h-4 w-4 mr-2" /> Exportar Excel
        </Button>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-auto">
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Competência</label>
            <select className="w-full sm:w-48 block mt-1 border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white min-h-[44px]" value={competence} onChange={(e) => setCompetence(e.target.value)} data-testid="filter-competence">
              {(summary?.by_competence || []).length === 0 && <option value="">Sem dados</option>}
              {(summary?.by_competence || []).map((c) => (
                <option key={c.competence} value={c.competence}>
                  {_isOddCompetence(c.competence) ? "⚠ " : ""}{monthLabel(c.competence)} — {c.total} casos
                </option>
              ))}
            </select>
          </div>
          <div className="w-full sm:w-auto">
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Hospital</label>
            <select className="w-full sm:w-56 block mt-1 border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white min-h-[44px]" value={hospitalId} onChange={(e) => setHospitalId(e.target.value)} data-testid="filter-hospital">
              <option value="">Todos os hospitais</option>
              {hospitals.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          </div>
          {isAdmin && competence && (
            <Button
              variant="outline"
              onClick={fixCompetence}
              data-testid="btn-fix-competence"
              className={`h-11 md:h-9 w-full sm:w-auto ${_isOddCompetence(competence)
                ? "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100"
                : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}
              title="Reatribuir todos os casos desta competência para outro mês (ex: corrigir 'Abr/0006' para 'Abr/2026')"
            >
              <Wrench className="h-3.5 w-3.5 mr-1.5" />
              {_isOddCompetence(competence) ? "Corrigir data desta competência" : "Reatribuir competência"}
            </Button>
          )}
          <div className="text-xs text-slate-500 ml-auto self-center">
            Competência é derivada do <code>billing_competence</code> do caso ou do ano-mês da cirurgia.
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-sm"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" /> Aprovado</div>
          <div className="text-2xl font-display font-bold text-slate-900 mt-1" data-testid="kpi-approved">{brl(selected?.approved ?? totals.ap)}</div>
        </CardContent></Card>
        <Card className="border-slate-200 shadow-sm"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> Faturado</div>
          <div className="text-2xl font-display font-bold text-slate-900 mt-1" data-testid="kpi-billed">{brl(selected?.billed ?? totals.fat)}</div>
        </CardContent></Card>
        <Card className="border-slate-200 shadow-sm"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Recebido</div>
          <div className="text-2xl font-display font-bold text-emerald-700 mt-1" data-testid="kpi-received">{brl(selected?.received ?? totals.rec)}</div>
        </CardContent></Card>
        <Card className="border-slate-200 shadow-sm"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5"><AlertCircle className="h-3.5 w-3.5" /> Glosa/Ajuste</div>
          <div className="text-2xl font-display font-bold text-red-600 mt-1" data-testid="kpi-adjust">{brl(selected?.adjustments ?? totals.adj)}</div>
        </CardContent></Card>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-3">Código</th>
                <th className="text-left px-4 py-3">Data</th>
                <th className="text-left px-4 py-3">Paciente</th>
                <th className="text-left px-4 py-3">Cirurgião</th>
                <th className="text-center px-4 py-3" title="Quantos cirurgiões dividem a fatia da equipe neste caso">Equipe</th>
                <th className="text-left px-4 py-3">Procedimento</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Aprovado</th>
                <th className="text-right px-4 py-3">Faturado</th>
                <th className="text-right px-4 py-3">Recebido</th>
                <th className="text-right px-4 py-3">Glosa</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={11} className="text-center p-6 text-slate-400">Carregando…</td></tr>}
              {!loading && cases.length === 0 && <tr><td colSpan={11} className="text-center p-8 text-slate-400">Nenhum caso nessa competência.</td></tr>}
              {cases.map((c, idx) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50 cursor-pointer" onClick={() => nav(`/casos/${c.id}`)} data-testid={`billing-row-${idx}`}>
                  <td className="px-4 py-3 font-mono text-xs">{c.case_code}</td>
                  <td className="px-4 py-3">{fmtDate(c.surgery_date)}</td>
                  <td className="px-4 py-3 font-medium text-slate-900 truncate max-w-[160px]">{c.patient_name || "—"}</td>
                  <td className="px-4 py-3 truncate max-w-[140px]">{c.surgeon_name || "—"}</td>
                  <td className="px-4 py-3 text-center">
                    {c.n_surgeons ? (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                          c.n_surgeons === 1
                            ? "bg-amber-50 text-amber-800 border border-amber-200"
                            : c.n_surgeons === 2
                              ? "bg-blue-50 text-blue-800 border border-blue-200"
                              : "bg-violet-50 text-violet-800 border border-violet-200"
                        }`}
                        title={`${c.n_surgeons} cirurgião(ões) — divisão da fatia da equipe ÷${c.n_surgeons}`}
                        data-testid={`badge-billing-n-${idx}`}
                      >
                        👥 {c.n_surgeons}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 truncate max-w-[180px]">{c.procedure_name || "—"}</td>
                  <td className="px-4 py-3 text-xs">{STATUS_LABELS[c.status] || c.status?.replace(/_/g, " ")}</td>
                  <td className="px-4 py-3 text-right">{brl(c.approved)}</td>
                  <td className="px-4 py-3 text-right font-medium">{brl(c.billed)}</td>
                  <td className="px-4 py-3 text-right text-emerald-700 font-medium">{brl(c.received)}</td>
                  <td className="px-4 py-3 text-right text-red-600">{brl(c.adjustments)}</td>
                </tr>
              ))}
            </tbody>
            {cases.length > 0 && (
              <tfoot className="bg-slate-50 border-t-2 border-slate-200 text-sm font-semibold">
                <tr>
                  <td colSpan={7} className="px-4 py-3 text-right text-slate-500 text-xs uppercase tracking-wider">Totais</td>
                  <td className="px-4 py-3 text-right">{brl(totals.ap)}</td>
                  <td className="px-4 py-3 text-right">{brl(totals.fat)}</td>
                  <td className="px-4 py-3 text-right text-emerald-700">{brl(totals.rec)}</td>
                  <td className="px-4 py-3 text-right text-red-600">{brl(totals.adj)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </CardContent>
      </Card>

      {summary?.by_surgeon?.length > 0 && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <h2 className="font-display text-lg font-semibold text-slate-900">Faturamento por cirurgião (geral)</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Cirurgião</th>
                    <th className="text-right px-3 py-2">Casos</th>
                    <th className="text-right px-3 py-2">Aprovado</th>
                    <th className="text-right px-3 py-2">Faturado</th>
                    <th className="text-right px-3 py-2">Recebido</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.by_surgeon.slice(0, 10).map((s, i) => (
                    <tr key={s.id || i} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-medium text-slate-900">{s.name}</td>
                      <td className="px-3 py-2 text-right">{s.total}</td>
                      <td className="px-3 py-2 text-right">{brl(s.approved)}</td>
                      <td className="px-3 py-2 text-right">{brl(s.billed)}</td>
                      <td className="px-3 py-2 text-right text-emerald-700">{brl(s.received)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
